import type { FC } from "react";

import { FieldError } from "@/shared/components/ui/FieldError";
import { CurrencyInput } from "@/shared/components/ui/CurrencyInput";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { RadioGroup, RadioGroupItem } from "@/shared/components/ui/radio-group";
import { UploadZone } from "@/shared/components/ui/upload-zone";
import { ACCOUNT_TYPES } from "@/shared/constants/estateCategories";
import { currencyInput } from "@/shared/utils/currencyInput";

const VEHICLE_TYPES = ACCOUNT_TYPES["Vehicles"];

export interface VehicleFormValues {
  vehicleType: string;
  model: string;
  year: string;
  value: string;
  vin: string;
  hasLoan: string;
  titleFile: File | null;
}

export const EMPTY_VEHICLE_FORM: VehicleFormValues = {
  vehicleType: "",
  model: "",
  year: "",
  value: "",
  vin: "",
  hasLoan: "",
  titleFile: null,
};

export interface VehicleFieldErrors {
  vehicleType?: string;
  model?: string;
  year?: string;
  hasLoan?: string;
}

export function validateVehicleFields(form: VehicleFormValues): VehicleFieldErrors {
  const errors: VehicleFieldErrors = {};
  if (!form.vehicleType) errors.vehicleType = "Please select a vehicle type";
  if (!form.model.trim()) errors.model = "Please enter a model";
  if (!form.year.trim()) errors.year = "Please enter a year";
  if (!form.hasLoan) errors.hasLoan = "Please select an option";
  return errors;
}

export function vehicleToItemFields(form: VehicleFormValues) {
  const nameParts = form.model.trim().split(/\s+/);
  const make = nameParts[0] || undefined;
  const model = nameParts.slice(1).join(" ") || undefined;

  return {
    item_type: form.vehicleType || undefined,
    balance: parseFloat(form.value) || 0,
    metadata: {
      year: form.year ? parseInt(form.year) : undefined,
      make,
      model,
      vin: form.vin || undefined,
      body_style: form.vehicleType || undefined,
      has_loan: form.hasLoan || undefined,
    },
  };
}

export function buildVehicleName(form: VehicleFormValues): string {
  return `${form.year} ${form.model}`.trim();
}

export function parseVehicleToForm(
  itemName: string,
  itemType: string,
  balance: number,
  notes?: string,
  metadata?: Record<string, unknown>,
): VehicleFormValues {
  const firstSpace = itemName.indexOf(" ");
  const year = firstSpace > -1 ? itemName.slice(0, firstSpace) : itemName;
  const model = firstSpace > -1 ? itemName.slice(firstSpace + 1) : "";
  const vin = (metadata?.vin as string) || notes || "";

  return {
    vehicleType: itemType,
    model,
    year,
    value: balance ? String(balance) : "",
    vin,
    hasLoan: (metadata?.has_loan as string) || "",
    titleFile: null,
  };
}

interface VehicleFormProps {
  form: VehicleFormValues;
  onChange: (form: VehicleFormValues) => void;
  fieldErrors?: VehicleFieldErrors;
  showValue?: boolean;
  showTitleUpload?: boolean;
  existingTitleFileName?: string | null;
  onTitleRemoved?: () => void;
}

export const VehicleForm: FC<VehicleFormProps> = ({
  form,
  onChange,
  fieldErrors,
  showValue = true,
  showTitleUpload = true,
  existingTitleFileName,
  onTitleRemoved,
}) => {
  const valueInput = currencyInput(form.value, (val) => onChange({ ...form, value: val }));

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <Label className="text-sm font-medium" required>
          Vehicle type
        </Label>
        <Select
          value={form.vehicleType}
          onValueChange={(val) => onChange({ ...form, vehicleType: val })}
        >
          <SelectTrigger className={`text-sm ${fieldErrors?.vehicleType ? "border-error" : ""}`}>
            <SelectValue placeholder="Select vehicle type" />
          </SelectTrigger>
          <SelectContent>
            {VEHICLE_TYPES.map((type) => (
              <SelectItem key={type} value={type} className="text-sm">
                {type}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <FieldError message={fieldErrors?.vehicleType} />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <Label className="text-sm font-medium" required>
            Model
          </Label>
          <Input
            value={form.model}
            onChange={(e) => onChange({ ...form, model: e.target.value })}
            placeholder="e.g., Toyota Camry"
            className={fieldErrors?.model ? "border-error" : ""}
          />
          <FieldError message={fieldErrors?.model} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-sm font-medium" required>
            Year
          </Label>
          <Input
            value={form.year}
            onChange={(e) => onChange({ ...form, year: e.target.value.replace(/[^\d]/g, "") })}
            placeholder="e.g., 2019"
            inputMode="numeric"
            className={fieldErrors?.year ? "border-error" : ""}
          />
          <FieldError message={fieldErrors?.year} />
        </div>
      </div>

      <div className={showValue ? "grid grid-cols-2 gap-3" : "flex flex-col gap-4"}>
        {showValue && (
          <div className="flex flex-col gap-1.5">
            <Label className="text-sm font-medium">
              Approximate value <span className="text-tertiary">(optional)</span>
            </Label>
            <CurrencyInput
              value={valueInput.displayValue}
              onChange={valueInput.handleChange}
              placeholder="0.00"
            />
          </div>
        )}
        <div className="flex flex-col gap-1.5">
          <Label className="text-sm font-medium">
            VIN <span className="text-tertiary">(optional)</span>
          </Label>
          <Input
            value={form.vin}
            onChange={(e) => onChange({ ...form, vin: e.target.value })}
            placeholder="e.g., 1HGBH41JXMN109186"
          />
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-1">
          <Label required>Is there a loan on this vehicle?</Label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-3.5 w-3.5 text-tertiary" />
              </TooltipTrigger>
              <TooltipContent>
                Look for monthly auto loan payments on recent bank statements, or check the title
                for a lien holder. If you can't tell, choose "Not sure" and we'll help confirm.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <RadioGroup
          value={form.hasLoan}
          onValueChange={(val) => onChange({ ...form, hasLoan: val })}
          className="flex items-center gap-6"
        >
          {[
            { value: "yes", label: "Yes" },
            { value: "no", label: "No" },
            { value: "not_sure", label: "Not sure" },
          ].map((opt) => (
            <label key={opt.value} className="flex items-center gap-2 text-sm">
              <RadioGroupItem value={opt.value} />
              {opt.label}
            </label>
          ))}
        </RadioGroup>
        <FieldError message={fieldErrors?.hasLoan} />
      </div>

      {showTitleUpload && (
        <UploadZone
          label="Do you have the title?"
          labelSuffix="(optional)"
          labelTooltip="The vehicle title is the legal document that proves ownership. You can usually find it in a filing cabinet, safe, or glove compartment."
          file={form.titleFile}
          onFileSelect={(file) => onChange({ ...form, titleFile: file })}
          onFileRemove={() => {
            onChange({ ...form, titleFile: null });
            onTitleRemoved?.();
          }}
          existingFileName={existingTitleFileName}
        />
      )}
    </div>
  );
};
